import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UATConstants } from 'src/Common/Models/UATConstants';
import type { PromptResponse } from '../types/promptGallery.types';
import { mockPrompts } from '../data/mockPrompts';

/**
 * Feature flag to control whether to use mock data or real API
 * Set to false when backend endpoint is ready
 */
const USE_MOCK_DATA = true;

/**
 * Helper to get base URL from CommonServicesWebApiUrl
 */
const getBaseUrl = (): string => {
    return `${UATConstants.CommonServicesWebApiUrl}/api/v1`;
};

/**
 * RTK Query API for Prompt Gallery
 * Handles fetching system-defined prompts
 */
export const promptGalleryApi = createApi({
    reducerPath: 'promptGalleryApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '', // Base URL is dynamic, set per endpoint
        prepareHeaders: (headers) => {
            const token = sessionStorage.getItem('jwtTokenForApi');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['Prompts'],
    endpoints: (builder) => ({
        /**
         * Get all available prompts
         * Uses mock data if USE_MOCK_DATA is true, otherwise calls real API
         */
        getPrompts: builder.query<PromptResponse, void>({
            queryFn: async () => {
                // Use mock data if feature flag is enabled
                if (USE_MOCK_DATA) {
                    // Simulate network delay for realistic behavior
                    await new Promise(resolve => setTimeout(resolve, 300));
                    return { data: mockPrompts };
                }

                // Real API call (when backend is ready)
                // This will be enabled when USE_MOCK_DATA is set to false
                return { error: { status: 'FETCH_ERROR', error: 'Real API not implemented yet' } };
            },
            // Alternative implementation for when real API is ready:
            // query: () => {
            //     const baseUrl = getBaseUrl();
            //     return {
            //         url: `${baseUrl}/prompts`,
            //         method: 'GET',
            //     };
            // },
            providesTags: ['Prompts'],
        }),
    }),
});

/**
 * Export hooks for usage in components
 */
export const { useGetPromptsQuery } = promptGalleryApi;
